import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store/ducks';
import store from 'store';

import { Button } from 'components/Button';
import Loading from 'components/Loading';
import { Row, Column } from 'components/Layout/Grid';
import Text from 'components/Text';
import { useCustomEvent } from 'components/ApplicationInsights';
import toast from 'components/Toast/dispatch';
import { eventName } from 'components/ApplicationInsights/eventNames';
import NoteCorrection from 'pages/NoteCorrection';

import iconTrackingDisable from 'img/icon-tracking-disable.svg';
import iconClockDeliveryForecastDisable from 'img/icon-clock-delivery-forecast-disable.svg';
import infoImg from 'img/icon-stock-not-available.svg';
import iconDeliveryForecast from 'img/icon-delivery-forecast.svg';

import { DateOutputStr } from 'utils/DateOutput';
import { formatDateToISO_UTC, IsInLimitMonth } from 'utils/Date';
import CreateProtocolServices from 'services/createProtocol.service';

import './styles.scss';
import { isMobileDevice } from 'utils/ScreenUtils';

type BtnClassProps = {
  btnClass: string;
  btnStylesImg: {
    height: number,
    width: number;
    paddingTop?: string;
  };
  btnOnClick: () => void;
  testId?: string;
};

const ActionButton = (props: any) => {
  const dispatch = useDispatch();
  const isMobile = isMobileDevice();
  const download = useSelector((state: RootState) => state.download);

  const [loading, setLoading] = useState(false);

  const {
    trackingKey,
    label,
    tooltip,
    textButton,
    toolTipPlace,
    className,
    disableWidth,
    enableWidth,
    img,
    action,
    trackName,
    trackItem,
    noTracking,
    tab,
    isLink,
    data,
    dateLimit,
    testId,
    origin
  } = props;

  const componentName = `Button`;
  const tracking = useCustomEvent(trackingKey, label !== '' ? label : tooltip);
  const trackMenuClick = useCustomEvent(componentName, trackName || label || tooltip);

  async function onSendRequest(actionRequest: any) {
    if (!download?.viewOptions) {
      setLoading(true);
      await actionRequest();
      !trackItem && tracking({});
      trackName && !trackItem && trackMenuClick({});
      !noTracking && trackItem && trackMenuClick({ item: trackItem });
      setLoading(false);
    }
  }

  async function onChangeTab(actionTab: any) {
    actionTab();
  }

  function renderNoteCorrectionButton() {
    return IsInLimitMonth(dateLimit, 6);
  }

  function linkToNoteCorrection() {
    dispatch({
      type: 'OPEN_SLIDER',
      data: {
        title: 'Correção da nota fiscal',
        content: NoteCorrection,
        data: [data]
      },
    });
  }

  const isExpiredOrder = !renderNoteCorrectionButton() && isLink;
  const tooltipNoteCorrection = isExpiredOrder ?
    'Pedido fora da validade para solicitar carta de correção' : tooltip;

  const classNoteCorrection = isExpiredOrder ? 'disable' : '';

  const handleClickNoteCorrection = () => {
    renderNoteCorrectionButton() && isLink ?
      linkToNoteCorrection() :
      onSendRequest(action);
  };

  const ButtonVisibilityModel = () => {
    if (loading && !textButton) {
      return (
        <Row
          className="align-center justify-center"
          style={{ paddingLeft: `${label ? '18' : '25'}%` }}
        >
          <Loading onlySpinner />
        </Row>
      );
    }

    if (tab) {
      return (
        <ModelButtonDefault
          btnClass={`outline height-action-btn-tabs ${className || ''}`}
          btnStylesImg={{ height: 18, width: 18 }}
          btnOnClick={() => onChangeTab(action)}
          testId={testId}
        />
      );
    }

    if (textButton) {
      if (isMobile && origin !== 'toast') {
        return (
          <ModelButtonDefaultMobile
            btnClass={`outline ${className || ''}`}
            btnStylesImg={{
              height: 36,
              width: 36,
              paddingTop: '2px'
            }}
            btnOnClick={() => onSendRequest(action)}
            testId={testId}
          />
        );
      }

      return (
        <ModelButtonDefault
          btnClass={`outline ${className || ''}`}
          btnStylesImg={{
            height: 14,
            width: 22,
            paddingTop: '2px'
          }}
          btnOnClick={() => onSendRequest(action)}
          testId={testId}
        />
      );
    }

    return (
      <Button
        tooltip={tooltipNoteCorrection}
        toolTipPlace={toolTipPlace}
        className={`d-flex align-center justify-center px-0 ${className || ''} }`}
        onClick={() => handleClickNoteCorrection()}
        width={50}
        testId={testId}
      >
        {img &&
          <img
            src={img}
            alt=""
            style={{ width: 22 }}
            className={`${classNoteCorrection}`}
          />
        }
      </Button>
    );
  };

  const ModelButtonDefault = ({ btnClass, btnStylesImg, btnOnClick, testId }: BtnClassProps) => {
    return (
      <Button
        tooltip={tooltip}
        toolTipPlace={toolTipPlace}
        className={btnClass}
        width={(disableWidth === undefined || enableWidth) ?? 'auto'}
        onClick={btnOnClick}
        isRequesting={!!loading}
        spinnerColorClass="color-primary"
        trackName={trackName}
        trackAction={trackItem}
        testId={testId}
      >

        <Row className='align-center'>
          {img && (
            <Column className="w-100">
              <img
                src={img}
                alt=""
                style={btnStylesImg}
              />
            </Column>
          )}
          <Column className="w-100 justify-center">
            <Text size={12} className="color-primary font-bold px-1">
              {textButton}
            </Text>
          </Column>
        </Row>
      </Button>
    );
  };

  const ModelButtonDefaultMobile = ({ btnClass, btnStylesImg, btnOnClick, testId }: BtnClassProps) => {
    return (
      <Column className='w-20 align-center'>
        <Button
          tooltip={tooltip}
          toolTipPlace={toolTipPlace}
          className={btnClass}
          height={65}
          width={65}
          onClick={btnOnClick}
          isRequesting={!!loading}
          spinnerColorClass="color-primary"
          trackName={trackName}
          trackAction={trackItem}
          testId={testId}
        >
          {img && (
            <img
              src={img}
              alt=""
              style={btnStylesImg}
            />
          )}
        </Button>
        <Text size={10} className="color-primary pt-2 break-line text-center">
          {textButton}
        </Text>
      </Column>
    );
  };

  return (
    <ButtonVisibilityModel />
  );
};

function orderStatus(date: string) {
  toast.info(
    'Pedido em processo de entrega',
    `Este pedido tem previsão de entrega para o dia ${DateOutputStr(date, 'dd/MM/yyyy')}. 
     Para verificar o andamento da entrega, consulte o rastreio nos detalhes do pedido.`,
    infoImg
  );
}

function orderDelivered(orderNumber: string, date: string) {
  toast.info(
    'Previsão de entrega',
    `O pedido ${orderNumber} foi entregue na data ${DateOutputStr(date, 'dd/MM/yyyy')}.`,
    iconDeliveryForecast,
    "Fechar"
  );
}

function deliveryForecastStatus(orderNumber: string, date: string) {
  toast.info(
    'Previsão de entrega',
    `A data prevista de entrega para o pedido ${orderNumber} é ${DateOutputStr(date, 'dd/MM/yyyy')}. Acompanhe a posição de entrega utilizando o tracking presente no detalhe do pedido.`,
    iconDeliveryForecast,
    "Fechar"
  );
}

function notDeliveryForecastStatus(data: any, actionFunction: (data: any) => any, user?: RootState['user']) {
  if (CreateProtocolServices.isAutoAttendanceProtocolDisabled(user)) {
    CreateProtocolServices.showUnavailableDeliveryForecastInfo(data.protheusOrderSale);
    return;
  }

  toast.yesNoCustom(
    'Previsão de entrega',
    `Não foi possivel consultar a previsão de entrega do pedido ${data.protheusOrderSale}. Deseja abrir um protocolo para que nosso time de atendimento verifique a posição de entrega?`,
    () => actionFunction(data),
    iconDeliveryForecast,
    undefined,
    'Sim',
    'Não'
  );
}

function deliveryForecastIsLowerCurrentDate(data: any, actionFunction: (data: any) => any, user?: RootState['user']) {
  if (CreateProtocolServices.isAutoAttendanceProtocolDisabled(user)) {
    CreateProtocolServices.showDelayedDeliveryForecastInfo(data.protheusOrderSale);
    return;
  }

  toast.yesNoCustom(
    'Previsão de entrega',
    `Identificação que o pedido ${data.protheusOrderSale} está com sua entrega atrasada. Deseja abrir um protocolo para que nosso time de atendimento verifique a posição de entrega?`,
    () => actionFunction(data),
    iconDeliveryForecast,
    undefined,
    'Sim',
    'Não'
  );
}

const handleIconDisable = (data: any, tooltip: string | undefined, iconButton: string) => {
  if ((data.status !== 'ENTREGUE') &&
    (tooltip?.toLowerCase() === eventName.COMPROVANTE_DE_ENTREGA.toLowerCase() ||
      tooltip?.toLowerCase() === eventName.COMPROVANTE_DE_ENTREGA_REMESSA.toLowerCase())) {
    return iconTrackingDisable;
  }

  if (data.status === 'ENTREGUE' && (tooltip?.toLowerCase() === eventName.PREVISAO_DE_ENTREGA.toLowerCase())) {
    return iconClockDeliveryForecastDisable;
  }

  return iconButton;
};

const handleAction = (
  data: any,
  tooltip: string | undefined,
  actionFunction: (data: any) => any,
  user?: RootState['user']
) => {

  const notDelivered = data.status !== 'ENTREGUE';

  if (notDelivered &&
    (tooltip?.toLowerCase() === eventName.COMPROVANTE_DE_ENTREGA.toLowerCase() || tooltip?.toLowerCase() === eventName.COMPROVANTE_DE_ENTREGA_REMESSA.toLowerCase())) {
    return orderStatus(data.expectedDeliveryDate);
  }

  const isDeliveryForecastEqualTooltip = tooltip?.toLowerCase() === eventName.PREVISAO_DE_ENTREGA.toLowerCase();

  if (data.status === 'ENTREGUE' && isDeliveryForecastEqualTooltip) {
    return orderDelivered(data.protheusOrderSale, data.invoiceDeliveryDate);
  }

  if (notDelivered && isDeliveryForecastEqualTooltip) {
    if (data.expectedDeliveryDate === undefined) {
      return notDeliveryForecastStatus(data, actionFunction, user);
    }

    const today = formatDateToISO_UTC(new Date());
    const expectedDeliveryDate = formatDateToISO_UTC(new Date(data.expectedDeliveryDate));

    if (expectedDeliveryDate >= today) {
      return deliveryForecastStatus(data.protheusOrderSale, data.expectedDeliveryDate);
    }

    if (expectedDeliveryDate < today) {
      return deliveryForecastIsLowerCurrentDate(data, actionFunction, user);
    }
  }

  return actionFunction(data);
};

const ModelButton = (
  label: string,
  iconButton: string,
  actionFuncion: (data: any) => any,
  tooltip?: string,
  size?: number,
  trackItem?: string,
  trackName?: string,
  textButton?: string,
  toolTipPlace?: string,
  isLink?: boolean,
  testId?: string,
) => ({
  noFilter: true,
  label,
  size: size || 30,
  trackItem: trackItem || null,
  trackName: trackName || null,
  maxSize: size || undefined,
  isButton: true,
  Component: (data: any) => {
    const user = store.getState().user;

    return (
      <ActionButton
        trackingKey={data.trackingKey}
        trackItem={trackItem}
        trackName={trackName}
        tooltip={data.status !== 'ENTREGUE' &&
          (tooltip?.toLowerCase() === eventName.COMPROVANTE_DE_ENTREGA.toLowerCase() ||
            tooltip?.toLowerCase() === eventName.COMPROVANTE_DE_ENTREGA_REMESSA.toLowerCase()) ?
          'Pedido em processo de entrega' : tooltip
        }
        textButton={textButton}
        img={handleIconDisable(data, tooltip, iconButton)}
        action={() => handleAction(data, tooltip, actionFuncion, user)}
        size={size}
        label={label}
        toolTipPlace={toolTipPlace}
        isLink={isLink}
        data={data}
        dateLimit={data.billingDate}
        testId={testId}
      />
    );
  },
});

export default ModelButton;
export { ActionButton };